import React, { useState, forwardRef, useImperativeHandle, Ref, useEffect } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { api } from '@/api/client';
import { envStorage } from '@/utils/envCache';
import { Button } from "@/components/ui/button";
import { Eye, EyeOff } from "lucide-react";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {toast} from "sonner";
import {useNavigate} from "react-router-dom";
import AuthService from "@/services/auth";


// 表单数据类型定义
const userFormSchema = z.object({
    password: z
        .string()
        .min(6, "密码至少 6 个字符")
        .max(30, "密码最长 30 个字符")
        .regex(
            /^(?=.*[a-zA-Z])(?=.*\d).{6,30}$/,
            "密码需包含字母和数字，长度 6-30 字符"
        ),
});

type UserFormValues = z.infer<typeof userFormSchema>;

// AuthService.clearTokens();
// 组件属性定义
interface AddUserDialogProps {
    onSuccess?: (response: any) => void;
}

// 暴露给父组件的方法类型
interface DialogRef {
    open: () => void;
    close: () => void;
}

export const ResetPasswordDialog = forwardRef<DialogRef, AddUserDialogProps>(
    ({ onSuccess }, ref) => {
        const baseUrl = window.location.origin + window.location.pathname + '#/';
        const [isOpen, setIsOpen] = useState(true);
        const [isSubmitting, setIsSubmitting] = useState(false);
        const [errorMessage, setErrorMessage] = useState("");
        const [successMessage, setSuccessMessage] = useState("");
        const [showPassword, setShowPassword] = useState(false);
        const url = window.location.hash;
        const hash = url.split("#")[1];
        const token = hash.split("/reset-password/")[1];
        const [inviterInfo, setInviterInfo] = useState({
            teamId: undefined,
            email: undefined,
            password: undefined,
            token: undefined
        });
        const navigate = useNavigate()
        // 表单配置
        const form = useForm<UserFormValues>({
            resolver: zodResolver(userFormSchema),
            defaultValues: {
                password: "",
            },
        });

        // 暴露方法给父组件
        useImperativeHandle(ref, () => ({
            open: () => {
                setIsOpen(true);
                form.reset();
                setErrorMessage("");
            },
            close: () => setIsOpen(false)
        }));



        useEffect(() => {
            setInviterInfo( {
                ...inviterInfo,
                teamId: envStorage.getItem('resetTeamId'),
                email: envStorage.getItem('resetEmail'),
            })
        }, []);

        // 处理表单提交
        const onSubmit = async (data: UserFormValues) => {
            try {
                // const token = getTokenFromUrl();
                if (!token) {
                    setErrorMessage("未找到有效的token");
                    return;
                }

                setIsSubmitting(true);
                setErrorMessage("");

                const response = await api.post('/v1/user/password/reset',
                    null,
                    {
                        params: {
                            teamId: inviterInfo.teamId,
                            email: inviterInfo.email,
                            password: data.password,
                            token: token
                        },
                        headers: {
                            'Content-Type': 'application/x-www-form-urlencoded'
                        }
                    }
                );

                //onSuccess?.(response);
                toast.success("重置密码成功");
                setSuccessMessage("重置密码成功!")
            } catch (error) {
                setErrorMessage(error.data.msg || "网络异常，请稍后重试");
            } finally {
                setIsSubmitting(false);
            }
        };

        return (
            // 阻止背景点击和ESC关闭
            <Dialog
                open={isOpen}
                onOpenChange={(open) => {
                    // 仅允许通过内部逻辑关闭，不响应外部触发的关闭
                    if (!open) return;
                    setIsOpen(open);
                }}
            >
                <DialogContent
                    className="sm:max-w-[70vw] max-h-[70vh]"
                    style={{
                        width: '70vw',
                        height: '400px',
                        maxWidth: '500px',
                        maxHeight: 'none',
                        overflow: 'auto'
                    }}

                >
                    <DialogHeader>
                        <DialogTitle>重置密码</DialogTitle>
                        <DialogDescription>
                            {/*请填寫激活信息，帶 * 的為必填項*/}
                        </DialogDescription>
                    </DialogHeader>

                    {errorMessage && (
                        <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-md text-sm">
                            {errorMessage}
                        </div>
                    )}

                    {/* 邀请人信息展示（只读） */}
                    {inviterInfo && (
                        <div className="mb-6 p-4 bg-gray-50 rounded-md">
                            <h4 className="text-sm font-medium text-gray-500 mb-2">{inviterInfo.email} 请输入您的新密码</h4>
                        </div>
                    )}

                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                            {/* 密码字段 */}
                            <FormField
                                control={form.control}
                                name="password"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>新密码 *</FormLabel>
                                        <div className="relative">
                                            <FormControl>
                                                <Input
                                                    placeholder="请输入密码"
                                                    type={showPassword ? "text" : "password"}
                                                    {...field}
                                                />
                                            </FormControl>
                                            <button
                                                type="button"
                                                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                                onClick={() => setShowPassword(!showPassword)}
                                            >
                                                {showPassword ? (
                                                    <EyeOff className="h-5 w-5 text-gray-500" />
                                                ) : (
                                                    <Eye className="h-5 w-5 text-gray-500" />
                                                )}
                                            </button>
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <DialogFooter>
                               {/* <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setIsOpen(false)}
                                    disabled={isSubmitting}
                                >
                                    取消
                                </Button>*/}
                                {!successMessage && (
                                    <Button
                                        type="submit"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? "提交中..." : "重置密码"}
                                    </Button>
                                )}
                            </DialogFooter>
                        </form>
                    </Form>

                    {/* 激活成功 */}
                    {successMessage && (
                        /*<div className="mb-6 p-4 bg-emerald-50	rounded-md">
                            <h4 className="text-sm font-medium text-gray-600 mb-2">激活成功! 可以访问 <a href={baseUrl} className="text-blue-600">{baseUrl}</a> 登录啦</h4>
                        </div>*/
                        <Button style={{height: '45px'}} onClick={() => {
                            // 触发自定义事件来打开登录对话框
                            setIsOpen(false)
                            window.dispatchEvent(new CustomEvent('openLoginDialog'));
                        }}>前往登录</Button>
                    )}
                </DialogContent>
            </Dialog>
        );
    }
);
